/**
 * Copyright © 2026 Rémi Pace.
 * This file is part of Abc-Map.
 *
 * Abc-Map is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of
 * the License, or (at your option) any later version.
 *
 * Abc-Map is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General
 * Public License along with Abc-Map. If not, see <https://www.gnu.org/licenses/>.
 */

import type { LoadFunction } from 'ol/Tile';
import { Logger } from '@abc-map/shared';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { TileStorage } from '../../storage/indexed-db/tiles/TileIDBStorage';
import { setTileImage } from './setTileImage';
import { setTileError } from './setTileError';
import { CURRENT_VERSION } from '../../storage/indexed-db/tiles/TileIDBEntry';

export const logger = Logger.get('tileLoadingPublic.ts');

export function tileLoadingPublic(): LoadFunction {
  const httpClient = axios.create({ timeout: 10_000, responseType: 'blob' });

  return async function (tile, src) {
    const storage = TileStorage.get();

    storage
      .get(src)
      // If storage fails, we try a network request
      .catch((err) => logger.warn('Cannot load tile from storage: ', err))
      .then<Blob>((tile) => {
        if (!tile) {
          return httpClient.get(src).then((res) => res.data);
        }
        return tile;
      })
      .then((image) => {
        setTileImage(tile, image);
        return storage.put({ version: CURRENT_VERSION, url: src, image });
      })
      .catch((err: Error | AxiosError | undefined) => {
        logger.error('Cannot load tile: ', err);
        setTileError(tile, err);
      });
  };
}
